import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { PiRabbitBold } from 'react-icons/pi';
import { IoIosArrowForward } from "react-icons/io";
import { IoIosArrowBack } from "react-icons/io";
import './rescue.css';

const Rescue = () => {
    //const API_BASE_URL = "http://localhost:3001/zajky"; //DEVELOPEMENT
    const API_BASE_URL = "https://zz.obn.sav.sk/zajky"
    const [isMobile, setIsMobile] = useState(false);
    const [totalCount, setTotalCount] = useState(null);
    const [rabbits, setRabbits] = useState({
        readyForAdoption: [],
        vetPrep: [],
        adopted: []
    });
    const [loading, setLoading] = useState({
        initial: true,
        readyForAdoption: false,
        vetPrep: false,
        adopted: false
    });
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState({
        readyForAdoption: true,
        vetPrep: true,
        adopted: true
    });
    const [offset, setOffset] = useState({
        readyForAdoption: 0,
        vetPrep: 0,
        adopted: 0
    });
    const [selectedRabbit, setSelectedRabbit] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const statusMap = {
        readyForAdoption: 'looking_for_home',
        vetPrep: 'vet_prep',
        adopted: 'found_home'
    };
    const imageRef = useRef(null);
    const nameRef = useRef(null);
    const [sectionHeight, setSectionHeight] = useState(0);
    const [sectionMargin, setSectionMargin] = useState(0);

    useEffect(() => {
        if (!selectedRabbit) return;

        const updateHeight = () => {
            if (imageRef.current) {
                const imageHeight = imageRef.current.offsetHeight;
                setSectionHeight(imageHeight);
            }
        };

        const UpdateMargin = () => {
            if (nameRef.current) {
                const nameHeight = nameRef.current.offsetHeight;
                setSectionMargin(nameHeight);
            }
        };

        updateHeight();
        UpdateMargin();

        const resizeObserver = new ResizeObserver(updateHeight);
        if (imageRef.current) {
            resizeObserver.observe(imageRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [selectedRabbit, currentImageIndex]);

    useEffect(() => {
        const checkIfMobile = () => {
            const userAgentCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
            const viewportCheck = window.innerWidth <= 768;
            return userAgentCheck || viewportCheck;
        };

        const handleResize = () => {
            setIsMobile(checkIfMobile());
        };

        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchTotalCount = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rabbits/count/total`);
            setTotalCount(response.data.total);
        } catch (error) {
            console.error("Error fetching total rabbit count:", error);
        }
    };

    const fetchRabbits = async (category, initialLoad = false) => {
        try {
            setLoading(prev => ({ ...prev, [category]: true }));

            const response = await axios.get(`${API_BASE_URL}/api/rabbits/paginated`, {
                params: {
                    status: statusMap[category],
                    limit: 4,
                    offset: initialLoad ? 0 : offset[category]
                }
            });

            const newRabbits = response.data.rabbits.map(rabbit => ({
                ...rabbit,
                imageUrl: rabbit.image_data
                    ? `data:${rabbit.image_mime_type};base64,${rabbit.image_data}`
                    : null,
                contact: rabbit.contact_preference === 1
                    ? { type: 'phone', value: 'stanka.ozzachranzajka@gmail.com' }
                    : { type: 'link', value: 'https://www.facebook.com/share/1685igXaDF/?mibextid=wwXIfr' }
            }));

            setRabbits(prev => ({
                ...prev,
                [category]: initialLoad ? newRabbits : [...prev[category], ...newRabbits]
            }));

            setHasMore(prev => ({ ...prev, [category]: response.data.hasMore }));
            setOffset(prev => ({ ...prev, [category]: initialLoad ? 4 : prev[category] + 4 }));
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(prev => ({
                ...prev,
                [category]: false,
                initial: false
            }));
        }
    };

    const fetchFullRabbitDetails = async (id) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rabbits/${id}`);
            const rabbit = response.data;

            const images = [
                rabbit.image_data ? `data:${rabbit.image_mime_type};base64,${rabbit.image_data}` : null,
                rabbit.image_data2 ? `data:${rabbit.image_mime_type2};base64,${rabbit.image_data2}` : null,
                rabbit.image_data3 ? `data:${rabbit.image_mime_type3};base64,${rabbit.image_data3}` : null
            ].filter(Boolean);

            return {
                ...rabbit,
                images,
                currentImage: 0,
                contact: rabbit.contact_preference === 1
                    ? { type: 'phone', value: 'stanka.ozzachranzajka@gmail.com' }
                    : { type: 'link', value: 'https://www.facebook.com/share/1685igXaDF/?mibextid=wwXIfr' }
            };
        } catch (error) {
            console.error("Error fetching rabbit details:", error);
            throw error;
        }
    };

    useEffect(() => {
        axios.get("https://zachranzajka.sk/zajky/api/rabbits/cors-test")
            .then(res => console.log("CORS test response:", res.data))
            .catch(err => console.error("CORS test error:", err));
    }, []);

    useEffect(() => {
        fetchTotalCount();
        fetchRabbits('readyForAdoption', true);
        fetchRabbits('vetPrep', true);
        fetchRabbits('adopted', true);
    }, []);

    const loadMore = (category) => {
        fetchRabbits(category);
    };

    const openLightbox = async (rabbit) => {
        try {
            const fullDetails = await fetchFullRabbitDetails(rabbit.rabbit_id);
            setSelectedRabbit(fullDetails);
            setCurrentImageIndex(0);
            document.body.style.overflow = 'hidden';
        } catch (error) {
            setError("Nepodarilo sa načítať detaily o zajacikovi");
        }
    };

    const closeLightbox = () => {
        setSelectedRabbit(null);
        document.body.style.overflow = 'auto';
    };

    const nextImage = () => {
        setCurrentImageIndex(prev =>
            prev < selectedRabbit.images.length - 1 ? prev + 1 : 0
        );
    };

    const prevImage = () => {
        setCurrentImageIndex(prev =>
            prev > 0 ? prev - 1 : selectedRabbit.images.length - 1
        );
    };

    const formatDescription = (description) => {
        if (!description) return null;

        const paragraphs = description.split(/\/n|\n/).filter(para => para.trim() !== '');

        return paragraphs.map((paragraph, index) => (
            <p key={index} className="description-block">
                {paragraph}
            </p>
        ));
    };

    if (loading.initial) {
        return <div className="gallery-loading">Hľadáme zajačikov...</div>;
    }

    if (error) {
        return <div className="gallery-error">Chyba pri hľadaní zajačikov: {error}</div>;
    }

    return (
        <div className="gallery-main">
            <div className={`gallery-container ${selectedRabbit ? 'blurred' : ''}`}>
                <section className="gallery-status-section">
                    <h1 className="gallery-section-title">ZAJKOVIA PRIPRAVENÍ NA ADOPCIU</h1>
                    {totalCount !== null && (
                        <div className="total-count-banner">
                            <span className='total-count-text'>Počet zachránených zajkov:</span>
                            <div className="total-count-number-wrapper">
                                <PiRabbitBold className="rabbit-icon" />
                                <span className='total-count-number'>{totalCount + 80}</span>
                                <PiRabbitBold className="rabbit-icon" />
                            </div>
                        </div>
                    )}
                    <div className="help-section-divider">
                        <div className="home-divider-left"></div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-image">
                            <img src="./images/carrot.png" />
                        </div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-right"></div>
                    </div>
                    {loading.readyForAdoption && rabbits.readyForAdoption.length === 0 ? (
                        <p className="gallery-loading-message">Načítavame zajačikov...</p>
                    ) : rabbits.readyForAdoption.length > 0 ? (
                        <>
                            <div className="gallery-grid">
                                {rabbits.readyForAdoption.map(rabbit => (
                                    <RabbitCard key={rabbit.rabbit_id} rabbit={rabbit} onClick={openLightbox} />
                                ))}
                            </div>
                            {hasMore.readyForAdoption && (
                                <button
                                    className="gallery-load-more"
                                    onClick={() => loadMore('readyForAdoption')}
                                    disabled={loading.readyForAdoption}
                                >
                                    <PiRabbitBold />
                                    {loading.readyForAdoption ? 'Načítava sa...' : 'Načítať ďalších'}
                                    <PiRabbitBold />
                                </button>
                            )}
                        </>
                    ) : (
                        <p className="gallery-empty-message">Momentálne nemáme žiadnych zajacikov pripravených na adopciu.</p>
                    )}
                </section>

                <section className="gallery-status-section">
                    <h1 className="gallery-section-title">UZDRAVUJÚ SA</h1>
                    <div className="help-section-divider">
                        <div className="home-divider-left"></div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-image">
                            <img src="./images/carrot.png" />
                        </div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-right"></div>
                    </div>
                    {loading.vetPrep && rabbits.vetPrep.length === 0 ? (
                        <p className="gallery-loading-message">Načítavame zajačikov...</p>
                    ) : rabbits.vetPrep.length > 0 ? (
                        <>
                            <div className="gallery-grid">
                                {rabbits.vetPrep.map(rabbit => (
                                    <RabbitCard key={rabbit.rabbit_id} rabbit={rabbit} onClick={openLightbox} />
                                ))}
                            </div>
                            {hasMore.vetPrep && (
                                <button
                                    className="gallery-load-more"
                                    onClick={() => loadMore('vetPrep')}
                                    disabled={loading.vetPrep}
                                >
                                    <PiRabbitBold />
                                    {loading.vetPrep ? 'Načítava sa...' : 'Načítať ďalších'}
                                    <PiRabbitBold />
                                </button>
                            )}
                        </>
                    ) : (
                        <p className="gallery-empty-message">Všetci zajkovia sú zdraví a v poriadku!</p>
                    )}
                </section>

                <section className="gallery-status-section">
                    <h1 className="gallery-section-title">ADOPTOVANÍ ZAJKOVIA</h1>
                    <div className="help-section-divider">
                        <div className="home-divider-left"></div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-image">
                            <img src="./images/carrot.png" />
                        </div>
                        <div className="home-divider-dot"></div>
                        <div className="home-divider-right"></div>
                    </div>
                    {loading.adopted && rabbits.adopted.length === 0 ? (
                        <p className="gallery-loading-message">Načítavame zajačikov...</p>
                    ) : rabbits.adopted.length > 0 ? (
                        <>
                            <div className="gallery-grid">
                                {rabbits.adopted.map(rabbit => (
                                    <RabbitCard key={rabbit.rabbit_id} rabbit={rabbit} onClick={openLightbox} />
                                ))}
                            </div>
                            {hasMore.adopted && (
                                <button
                                    className="gallery-load-more"
                                    onClick={() => loadMore('adopted')}
                                    disabled={loading.adopted}
                                >
                                    <PiRabbitBold />
                                    {loading.adopted ? 'Načítava sa...' : 'Načítať ďalších'}
                                    <PiRabbitBold />
                                </button>
                            )}
                        </>
                    ) : (
                        <p className="gallery-empty-message">Ešte nemáme žiadne úspešné adopcie. Buďte prvý!</p>
                    )}
                </section>
            </div>

            {selectedRabbit && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <div className={`lightbox-content ${selectedRabbit.sex === 'male' ? 'lightbox-male' : 'lightbox-female'}`} onClick={e => e.stopPropagation()}>
                        <div className="lightbox-header">
                            <button className="lightbox-close" onClick={closeLightbox}>×</button>
                        </div>
                        <div className="lightbox-body">
                            <div className="lightbox-image-container">
                                <h3 className={`lightbox-name ${selectedRabbit.sex === 'male' ? 'name-male' : 'name-female'}`} ref={nameRef}>{selectedRabbit.name}</h3>
                                <div className="lightbox-image-wrapper">
                                    {selectedRabbit.images.length > 0 ? (
                                        <>
                                            <img
                                                src={selectedRabbit.images[currentImageIndex]}
                                                alt={selectedRabbit.name}
                                                className="lightbox-image"
                                                ref={imageRef}
                                            />
                                            {selectedRabbit.images.length > 1 && (
                                                <>
                                                    <div className="lightbox-image-nav">
                                                        <button
                                                            className="lightbox-nav-button prev"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                prevImage();
                                                            }}
                                                        >
                                                            <IoIosArrowBack />
                                                        </button>
                                                        <button
                                                            className="lightbox-nav-button next"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                nextImage();
                                                            }}
                                                        >
                                                            <IoIosArrowForward />
                                                        </button>
                                                    </div>
                                                    <div className="lightbox-dots">
                                                        {selectedRabbit.images.map((_, index) => (
                                                            <div
                                                                key={index}
                                                                className={`lightbox-dot ${index === currentImageIndex ? 'active' : ''}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setCurrentImageIndex(index);
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="lightbox-no-image">Žiadny obrázok</div>
                                    )}
                                </div>
                                <div className="lightbox-info-grid">
                                    <div className="lightbox-info-item">
                                        <p className="lightbox-info-label">Vek:</p>
                                        <p className="lightbox-info-value">{formatAge(selectedRabbit.age, selectedRabbit.date_added)}</p>
                                    </div>
                                    <div className="lightbox-info-item">
                                        <p className="lightbox-info-label">Pohlavie:</p>
                                        <p className="lightbox-info-value">{selectedRabbit.sex === 'male' ? 'Samček' : 'Samička'}</p>
                                    </div>
                                    {/*<div className="lightbox-info-item">
                                            <p className="lightbox-info-label">Kastrácia:</p>
                                            <p className={`lightbox-info-value ${selectedRabbit.is_neutered ? 'positive' : 'negative'}`}>
                                                {selectedRabbit.is_neutered ? '✓ Kastrovaný' : '✗ Nekastrovaný'}
                                            </p>
                                        </div>
                                        <div className="lightbox-info-item">
                                            <p className="lightbox-info-label">Vakcinácia:</p>
                                            <p className={`lightbox-info-value ${selectedRabbit.is_vaccinated ? 'positive' : 'negative'}`}>
                                                {selectedRabbit.is_vaccinated ? '✓ Vakcinovaný' : '✗ Nevakcinovaný'}
                                            </p>
                                        </div>*/}
                                </div>
                            </div>
                            <div className="lightbox-details" style={!isMobile ? { height: `${sectionHeight}px`, marginTop: `${sectionMargin - 20}px` } : { height: 'auto', marginTop: '0px' }}>
                                <div className="lightbox-info-container">
                                    {selectedRabbit.slogan && (
                                        <div className="lightbox-slogan">
                                            <p className="lightbox-slogan-text">{selectedRabbit.slogan}</p>
                                        </div>
                                    )}
                                </div>
                                {selectedRabbit.description && (
                                    <div className="lightbox-description-container">
                                        {formatDescription(selectedRabbit.description)}
                                    </div>
                                )}
                                <div className="lightbox-divider-wrapper">
                                    <div className="lightbox-section-divider">
                                        <div className="lightbox-divider-left"></div>
                                        <div className="lightbox-divider-dot"></div>
                                        <div className="lightbox-divider-image">
                                            <img src="./images/carrot.png" />
                                        </div>
                                        <div className="lightbox-divider-dot"></div>
                                        <div className="lightbox-divider-right"></div>
                                    </div>
                                </div>
                                <div className="lightbox-contact">
                                    {selectedRabbit.contact_preference === 1 ? (
                                        <a
                                            rel="noopener noreferrer"
                                            className="lightbox-contact-button"
                                        >
                                            +421 940 156 949
                                        </a>
                                    ) : (
                                        <a
                                            href="https://www.facebook.com/share/1685igXaDF/?mibextid=wwXIfr"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="lightbox-contact-button"
                                        >
                                            Kontaktujte nás
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const calculateCurrentAge = (originalAge, dateAdded) => {
    if (!dateAdded) return parseFloat(originalAge); // If no date_added, return original age

    try {
        const addedDate = new Date(dateAdded);
        const currentDate = new Date();

        // Calculate difference in months
        const monthsSinceAdded = (currentDate.getFullYear() - addedDate.getFullYear()) * 12 +
            (currentDate.getMonth() - addedDate.getMonth());

        // Convert original age to months
        const originalAgeValue = parseFloat(originalAge);
        const originalAgeMonths = originalAgeValue < 1 ?
            Math.round(originalAgeValue * 100) : // For months (0.01 = 1 month)
            originalAgeValue * 12; // For years (1.00 = 12 months)

        // Calculate total age in months
        const totalAgeMonths = originalAgeMonths + monthsSinceAdded;

        // Return either months (0.XX) or years (X.00)
        if (totalAgeMonths < 12) {
            return parseFloat((totalAgeMonths / 100).toFixed(2)); // 0.01 - 0.11
        } else {
            const years = Math.round(totalAgeMonths / 12); // Round to nearest year
            return parseFloat(years.toFixed(2)); // X.00
        }
    } catch (e) {
        console.error("Error calculating age:", e);
        return parseFloat(originalAge); // Fallback to original age if date parsing fails
    }
};

const formatAge = (age, dateAdded = null) => {
    // First check if original age is 0.00
    const originalAgeValue = parseFloat(age);
    if (originalAgeValue === 0.00) {
        return 'Neznámy';
    }

    // Proceed with normal calculation for non-zero ages
    const currentAge = calculateCurrentAge(age, dateAdded);

    if (currentAge < 1) {
        const months = Math.round(currentAge * 100);
        if (months === 0) return 'Neznámy';
        if (months === 1) return '1 mesiac';
        if (months >= 2 && months <= 4) return `${months} mesiace`;
        return `${months} mesiacov`;
    }

    // Handle years
    const years = Math.round(currentAge); // Already in X.00 format from calculateCurrentAge

    if (years === 1) return '1 rok';
    if (years >= 2 && years <= 4) return `${years} roky`;
    return `${years} rokov`;
};

const RabbitCard = ({ rabbit, onClick }) => {
    const getCardColorClass = () => {
        if (rabbit.status === 'looking_for_home') {
            return rabbit.sex === 'male' ? 'male-card' : 'female-card';
        } else if (rabbit.status === 'vet_prep') {
            return 'vet-card';
        } else if (rabbit.status === 'found_home') {
            return 'adopted-card';
        }
        return '';
    };

    const handleClick = () => {
        if (rabbit.status === 'looking_for_home') {
            onClick(rabbit);
        }
    };

    return (
        <div
            className={`gallery-card ${getCardColorClass()} ${rabbit.status !== 'looking_for_home' ? 'non-clickable' : ''}`}
            onClick={handleClick}
        >
            <div className="gallery-image-container">
                {rabbit.imageUrl ? (
                    <img src={rabbit.imageUrl} alt={rabbit.name} className="gallery-image" />
                ) : (
                    <div className="gallery-no-image">Žiadny obrázok</div>
                )}
            </div>
            <div className="gallery-details">
                <h3 className="gallery-name">{rabbit.name}</h3>
                <p className="gallery-info"><strong>Vek:</strong> {formatAge(rabbit.age, rabbit.date_added)}</p>
                <p className="gallery-info"><strong>Pohlavie:</strong> {rabbit.sex === 'male' ? 'Samček' : 'Samička'}</p>
                {/*<p className={`gallery-vaccinated ${rabbit.is_vaccinated ? 'positive' : 'negative'}`}>
                    {rabbit.is_vaccinated ? '✓ Očkovaný' : '✗ Neočkovaný'}
                </p>
                <p className={`gallery-neutered ${rabbit.is_neutered ? 'positive' : 'negative'}`}>
                    {rabbit.is_neutered ? '✓ Kastrovaný' : '✗ Nekastrovaný'}
                </p>*/}
            </div>
        </div>
    );
};

export default Rescue;